export interface ReportFileNameParts {
  entityType: string;
  subject?: string | null;
  fallbackId?: string | number | null;
  sampleCount?: number;
  datePart: string;
  timePart: string;
  extension: string;
}

const SUBJECT_MAX_LENGTH = 60;
const FILE_NAME_MAX_LENGTH = 120;
const FALLBACK_ID_LENGTH = 8;
const EDGE_SEPARATORS = /^[-._]+|[-._]+$/g;
const FORBIDDEN_CHARS = /[/\\:*?"<>|\u007f\u200e\u200f\u2028\u2029\u202a-\u202e\u2066-\u2069]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f]/g;
const MULTI_SAMPLE = 'MultiSample';

const KIND_BY_ENTITY_TYPE: Record<string, string> = {
  SampleSurvey: 'Survey-Report',
  Sample: 'Sample-Report',
  EmployeeLetter: 'Employee-Letter',
  RiskRegister: 'Risk-Assessment'
};

const FALLBACK_WORD_BY_ENTITY_TYPE: Record<string, string> = {
  SampleSurvey: 'Survey',
  Sample: 'Sample',
  EmployeeLetter: 'Sample',
  RiskRegister: 'Risk-Assessment',
  MultiSample: 'Samples'
};

function truncate(value: string, maxCodePoints: number): string {
  return Array.from(value).slice(0, maxCodePoints).join('');
}

export function sanitizeReportFileNameSubject(raw: string | null | undefined): string {
  const cleaned = (raw ?? '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(CONTROL_CHARS, '')
    .replace(FORBIDDEN_CHARS, '')
    .replace(/-{2,}/g, '-')
    .replace(EDGE_SEPARATORS, '');
  return truncate(cleaned, SUBJECT_MAX_LENGTH).replace(EDGE_SEPARATORS, '');
}

export function buildReportFileName(parts: ReportFileNameParts): string {
  const kind = parts.entityType === MULTI_SAMPLE
    ? `${parts.sampleCount ?? 0}-Samples_Report`
    : KIND_BY_ENTITY_TYPE[parts.entityType] ?? 'Report';
  const subject = sanitizeReportFileNameSubject(parts.subject) || buildFallbackSubject(parts);
  const fixedLength = `${parts.datePart}__${kind}_${parts.timePart}.${parts.extension}`.length;
  const fittedSubject = truncate(subject, Math.max(FILE_NAME_MAX_LENGTH - fixedLength, 1)).replace(EDGE_SEPARATORS, '');
  return `${parts.datePart}_${fittedSubject || truncate(subject, 1)}_${kind}_${parts.timePart}.${parts.extension}`;
}

function buildFallbackSubject(parts: ReportFileNameParts): string {
  const word = FALLBACK_WORD_BY_ENTITY_TYPE[parts.entityType] ?? 'Report';
  const idPart = truncate(sanitizeReportFileNameSubject(parts.fallbackId?.toString()), FALLBACK_ID_LENGTH);
  return idPart ? `${word}-${idPart}` : word;
}
